import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import useAuth from "../../hooks/useAuth";
import { useNotification } from "../../hooks/useNotification";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import { notificationModalOpenState } from "../../recoil/notification";
import Dashboard from "./tabs/dashboard/Dashboard";
import DashboardMobile from "./tabs/dashboard/DashboardMobile";
import MyAdvertisement from "./tabs/myAdvertisement/MyAdvertisement";
import Settings from "./tabs/Settings";
import Deposit from "./tabs/deposit/Index";
import AddAdvertisement from "./tabs/addAdvertisement/AddAdvertisement";
import NotificationModal from "../../components/NotificationModal";
import {
  MdDashboard,
  MdOutlineAdsClick,
  MdMiscellaneousServices,
  MdAccountBalanceWallet,
  MdMenu,
  MdClose,
} from "react-icons/md";
import { IoBookOutline, IoChatbubbleEllipsesOutline } from "react-icons/io5";
import { BsQuestionCircle } from "react-icons/bs";
import "./HomeMobile.css";

const HomeMobile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [depositSubTab, setDepositSubTab] = useState("chargeRequest");
  const [tempDepositSubTab, setTempDepositSubTab] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // 알림 관련
  const { unreadCount, fetchNotifications } = useNotification();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useRecoilState(
    notificationModalOpenState
  );

  // Zoom Settings 관련
  const { settings } = useZoomSettings();

  // URL 파라미터에서 원하는 탭 확인
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const desiredTab = params.get("tab");
    const desiredSubTab = params.get("subTab");

    if (
      desiredTab &&
      [
        "dashboard",
        "users",
        "settings",
        "deposit",
        "addAdvertisement",
        "myAdvertisement",
      ].includes(desiredTab)
    ) {
      setActiveTab(desiredTab);

      // 서브탭이 있는 경우 처리
      if (desiredSubTab && desiredTab === "deposit") {
        setTempDepositSubTab(desiredSubTab);
      }
    } else {
      // URL 파라미터가 없으면 기본적으로 대시보드로 설정하고 URL 업데이트
      setActiveTab("dashboard");
      navigate("/?tab=dashboard", { replace: true });
    }
  }, [location.search, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
    setIsSidebarOpen(false);
  };

  const handleGuideClick = () => {
    alert("이용 가이드 링크를 준비 중입니다.");
  };

  const handleFAQClick = () => {
    alert("자주묻는질문 링크를 준비 중입니다.");
  };

  const handleKakaoChat = () => {
    if (settings?.kakaoChannelId) {
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      alert("카카오톡 상담 링크를 준비 중입니다.");
    }
  };

  const handleNotificationClick = async () => {
    await fetchNotifications(1);
    setIsNotificationModalOpen(true);
  };

  const handleTabClick = (tabName, subTab = null) => {
    // 모바일에서 탭 클릭 시 사이드바 닫기
    setIsSidebarOpen(false);

    // 대시보드는 로그인 없이도 접근 가능
    if (tabName === "dashboard") {
      setActiveTab(tabName);
      navigate(`/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}`);
      return;
    }

    // 예치금 관리 탭으로 이동하면서 서브탭 지정
    if (tabName === "deposit" && subTab) {
      setTempDepositSubTab(subTab);
    } else {
      setTempDepositSubTab(null);
    }

    setActiveTab(tabName);
    navigate(`/?tab=${tabName}${subTab ? `&subTab=${subTab}` : ""}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardMobile onTabChange={handleTabClick} />;
      case "addAdvertisement":
        return <AddAdvertisement onTabChange={handleTabClick} />;
      case "myAdvertisement":
        return <MyAdvertisement />;
      case "settings":
        return <Settings />;
      case "deposit":
        return <Deposit initialTab={tempDepositSubTab || depositSubTab} />;
      default:
        return <Dashboard onTabChange={handleTabClick} />;
    }
  };

  return (
    <div className="home-mobile-container">
      <Helmet>
        <title>올려줌 - 모바일</title>
        <meta name="description" content="올려줌 모바일 - 셀프 마케팅 플랫폼" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* 헤더 */}
      <div className="home-mobile-header">
        <div className="home-mobile-header-left">
          <button
            className="home-mobile-hamburger-btn"
            onClick={() => setIsSidebarOpen(true)}
          >
            <MdMenu />
          </button>
          <h1 className="home-mobile-logo">올려줌</h1>
        </div>
        <div className="home-mobile-header-right">
          <button
            className="home-mobile-notification-btn"
            onClick={handleNotificationClick}
            style={{ position: "relative" }}
          >
            🔔
            {unreadCount > 0 && (
              <span className="home-mobile-notification-badge">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="home-mobile-main-content">{renderTabContent()}</div>

      {/* 사이드바 오버레이 */}
      <div
        className={`home-mobile-overlay ${isSidebarOpen ? "open" : ""}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* 모바일 사이드바 */}
      <div className={`home-mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        <div className="home-mobile-sidebar-header">
          <h2>메뉴</h2>
          <button
            className="home-mobile-sidebar-close"
            onClick={() => setIsSidebarOpen(false)}
          >
            <MdClose />
          </button>
        </div>

        {/* 사용자 정보 섹션 */}
        <div className="home-mobile-user-section">
          {user ? (
            <>
              <div className="home-mobile-user-info">
                {/* <div className="home-mobile-user-avatar">
                  {user.name?.charAt(0) || "U"}
                </div> */}
                <div className="home-mobile-user-details">
                  <span className="home-mobile-user-name">
                    안녕하세요, {user.name}님!
                  </span>
                </div>
              </div>

              {user?.price_grade?.name && (
                <div className="home-mobile-grade-tag">
                  {user.price_grade.name}
                </div>
              )}

              {user?.deposit !== undefined && (
                <div className="home-mobile-deposit-info">
                  <span className="home-mobile-money-icon">💰</span>
                  <span>예치금: {user.deposit.toLocaleString()} 원</span>
                </div>
              )}

              <button onClick={handleLogout} className="home-mobile-logout-btn">
                로그아웃
              </button>
            </>
          ) : (
            <div className="home-mobile-login-prompt">
              <span>로그인이 필요합니다</span>
              <button
                onClick={() => {
                  navigate("/login");
                  setIsSidebarOpen(false);
                }}
                className="home-mobile-login-btn"
              >
                로그인
              </button>
            </div>
          )}
        </div>

        {/* 구분선 */}
        <div className="home-mobile-divider" />

        {/* 네비게이션 메뉴 */}
        <nav className="home-mobile-nav">
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "dashboard" ? "active" : ""
            }`}
            onClick={() => handleTabClick("dashboard")}
          >
            <MdDashboard />
            <span>대시보드</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "addAdvertisement" ? "active" : ""
            } ${!user ? "disabled" : ""}`}
            onClick={() => handleTabClick("addAdvertisement")}
          >
            <MdMiscellaneousServices />
            <span>광고 등록</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "myAdvertisement" ? "active" : ""
            } ${!user ? "disabled" : ""}`}
            onClick={() => handleTabClick("myAdvertisement")}
          >
            <MdOutlineAdsClick />
            <span>광고 확인</span>
          </button>
          <button
            className={`home-mobile-nav-btn ${
              activeTab === "deposit" ? "active" : ""
            } ${!user ? "disabled" : ""}`}
            onClick={() => handleTabClick("deposit")}
          >
            <MdAccountBalanceWallet />
            <span>예치금 관리</span>
          </button>
        </nav>

        {/* 하단 메뉴 */}
        <div className="home-mobile-footer-menu">
          <button className="home-mobile-footer-btn" onClick={handleGuideClick}>
            <IoBookOutline />
            <span>이용 가이드</span>
          </button>
          <button className="home-mobile-footer-btn" onClick={handleFAQClick}>
            <BsQuestionCircle />
            <span>자주묻는질문</span>
          </button>
          <button className="home-mobile-footer-btn" onClick={handleKakaoChat}>
            <IoChatbubbleEllipsesOutline />
            <span>카카오톡 상담하기</span>
          </button>
        </div>
      </div>

      {/* 알림 모달 */}
      <NotificationModal />
    </div>
  );
};

export default HomeMobile;
